import React, { useCallback, useEffect, useRef, useState } from 'react';
import { down, touch } from '../../../../style-utils/media-queries';
import { ConfirmDialog } from '../../../modal/confirm-dialog';
import styled from '@emotion/styled';
import { Link, useSearchParams } from 'react-router-dom';
import { getStaysBilling } from '../../../../api/stays-billing-client';
import { useResource } from '../../../hooks/use-resource';
import {
  ListSkeleton,
  LoadError,
} from '../dashboard-shared-components/load-state';
import { Staleable, StateSwitch } from '../../../../motion';
import toast from '../../../../utils/toast';
import axios from 'axios';
import { apiRoute } from '../../../../constants/api-constants';
import { fetchAllItems } from '../../../requests/requests';
import { useAuth } from '../../../../context/auth-provider';
import { deskInPlan } from '../../../../utils/products';
import { can, PERMISSIONS } from '../../../../auth/permissions';
import {
  palette,
  spacing,
  radius,
  shadow,
  layout,
  status,
  typography,
} from '../../../../style-utils/theme';
import { AppButton } from '../../../button/app-button';
import { IqBillingRow } from '../../../iq/iq-billing-row';
import { StripeCardModal } from '../../../payments/stripe-card-modal';
import { useUnitPurchase } from '../../../payments/use-unit-purchase';
import { usePlanChange } from '../../../payments/use-plan-change';
import {
  cancelScheduledChange,
  compedRefusal,
  PlanChangeResult,
  reconcileCheckout,
  startCheckout,
} from '../../../../api/plan-client';
import {
  BillingInterval,
  IntervalToggle,
} from '../../../billing/interval-toggle';
import { annualBadgePercent } from '../../../billing/plan-display';
import { formatDate } from '../../../../function-utils/locale';
import { ReturnUnitsDialog } from '../../../units/return-units-dialog';
import { RestoreUnitsDialog } from '../../../units/restore-units-panel';
import { getReturnedUnits } from '../../../../api/units-client';
import { ProductsCard } from '../../../billing/products-card';

interface Plan {
  id: number;
  code: string;
  name: string;
  description: string;
  max_buildings?: number | null;
  price_cents: number;
  currency: string;
  billing_interval: string;
  is_per_unit: boolean;
  contact_sales: boolean;
  iq_enabled: boolean;
  // Migration 105: a row is one PRICE. `legacy` = a grandfathered price the
  // org keeps and nobody new can choose. The rest is the server's own
  // monthly/annual arithmetic — nothing here derives a saving.
  tier?: string | null;
  legacy?: boolean;
  counterpart_plan_id?: number | null;
  effective_monthly_cents?: number | null;
  annual_savings_badge_percent?: number | null;
  // IS6: 'bundle' is Desk + Stays at one price, changed only from "Your products".
  product_line?: string;
}

interface Sub {
  plan_id: number;
  building_limit?: number | null;
  unit_quantity?: number | null;
  units_used?: number | null;
  units_allowed?: number | null;
  units_remaining?: number | null;
  // How far PAST the purchased pool the org is, computed server-side. Optional
  // because a browser holding this bundle may be talking to a server that does
  // not report it yet — absent reads the same as zero, i.e. nothing to say.
  units_over?: number | null;
  // Archived (previously returned) units the org could restore. Optional for
  // the same rollout reason; absent reads as zero.
  units_returned?: number | null;
  status: string;
  // A COMPLIMENTARY account (FREE_SIGNUP, migration 110). Already on the wire —
  // plans.js spreads sub.toJSON() — and it turns this screen from a money
  // screen into a statement: there is no Stripe customer, no card and nothing
  // renews, and the server refuses every billing change with 409
  // COMPED_ACCOUNT. Optional: an older server does not send it.
  comped?: boolean | null;
  // Already on the wire (plans.js spreads sub.toJSON()); simply never typed.
  iq_enabled?: boolean;
  // The Stripe subscription a promotion is applied to. Also already on the
  // wire, and already handled by the browser elsewhere (signup returns it and
  // POST /subscriptions/apply-promo takes it): it is an object id, not a
  // credential, and the server re-checks that it belongs to the caller's own
  // organisation before it touches it.
  stripe_subscription_id?: string | null;
  cancel_at_period_end?: boolean | null;
  current_period_end?: string | null;
  // The promotion this subscription is getting, read by the server from the
  // redemption ledger. Null when none is running.
  promotion?: {
    code: string;
    description: string;
    discount_percent: number;
    duration: string;
    ends_at: string | null;
  } | null;
  trial_end?: string | null;
  plan?: Plan | null;
  // A SCHEDULED interval change (annual -> monthly at the end of the paid
  // year) — the local mirror of the Stripe schedule.
  scheduled_plan_id?: number | null;
  scheduled_change_at?: string | null;
  scheduled_plan?: {
    id: number;
    name: string;
    billing_interval: string;
    price_cents: number;
    currency: string;
  } | null;
}

const intervalWord = (iv?: string | null) =>
  iv === 'year' ? 'Annual' : 'Monthly';

// A complimentary account's refusal is named for what it is before the
// server's own words are read (api/plan-client.ts, compedRefusal).
const errMessage = (e: unknown, fallback: string): string =>
  compedRefusal(e) ||
  (axios.isAxiosError(e) && e.response?.data?.message
    ? String(e.response.data.message)
    : fallback);

interface CardOnFile {
  brand: string;
  last4: string;
  exp_month: number;
  exp_year: number;
}

interface Invoice {
  id: string;
  number: string | null;
  status: string;
  amount_paid: number;
  amount_due: number;
  currency: string;
  created: string | null;
  hosted_invoice_url: string | null;
  invoice_pdf: string | null;
}

/** Minor units to a readable amount, in the invoice's OWN currency. */
const money = (minor: number, currency: string): string =>
  new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: (currency || 'usd').toUpperCase(),
  }).format((minor || 0) / 100);

// A billing date: the reader's own clock (R-T6 — billing belongs to no building).
const onlyDate = (iso?: string | null): string => (iso ? formatDate(iso) || '—' : '—');

// The quote, the card and the commit all live in useUnitPurchase — one
// implementation shared with the out-of-units dialog, so the two doors into a
// unit purchase can never quote different numbers for the same change. See
// components/payments/use-unit-purchase.ts for the four-figure rule and why
// nothing in the frontend multiplies a rate by a count (D-12).

/**
 * THE SUBSCRIPTION STATE, IN WORDS THE PAYER USES.
 *
 * This printed the provider's raw enum into a muted meta line — "status:
 * past_due", "status: incomplete_expired", "status: trialing". Those are
 * Stripe's internal vocabulary, not a sentence, and `past_due` in grey 12px is
 * how an account quietly lapses: the one state that needs the reader to act
 * looked exactly like the four that do not.
 *
 * Unknown values fall through to a humanised form rather than being hidden — a
 * state nobody anticipated is still better shown than swallowed.
 */
const SUB_STATUS_COPY: Record<string, string> = {
  trialing: 'Free trial — no charge yet',
  active: 'Active',
  // "Update your card" pointed at nothing — there is no card form on this
  // screen. Paying the open invoice on Stripe's page is the way back, and it
  // also replaces the subscription's card (measured 2026-09-15: the declined
  // 0341 card was replaced by the one used to pay).
  past_due:
    'Payment failed — pay the open invoice below (it also updates your card on file) to avoid losing access',
  unpaid: 'Unpaid — access is at risk',
  canceled: 'Cancelled',
  incomplete: 'Waiting for payment confirmation',
  incomplete_expired: 'Setup expired — start again to reactivate',
  paused: 'Paused',
};

const describeSubStatus = (status?: string | null): string => {
  if (!status) return 'Status unknown';

  return (
    SUB_STATUS_COPY[status] ||
    status.replace(/_/g, ' ').replace(/^./, first => first.toUpperCase())
  );
};


/* A receipt list, not a table: four short facts per row that must stay readable
   when the column count collapses on a phone. */
const InvoiceList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
  margin: ${spacing.xs} 0 0;
  padding: 0;
  list-style: none;
`;

const InvoiceRow = styled.li`
  display: grid;
  grid-template-columns: auto auto 1fr auto;
  align-items: center;
  gap: ${spacing.sm};
  padding: ${spacing.half} 0;
  border-top: 1px solid ${palette.line};
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  color: ${palette.slate};

  a {
    color: ${palette.lagoon};
    font-weight: ${typography.weight.semibold};
  }

  ${down('sm')} {
    grid-template-columns: 1fr 1fr;
  }
`;

const InvoiceStatus = styled.span`
  text-transform: capitalize;
  color: ${palette.ink};
`;

/**
 * Independent Stays IS1 (IS-T9, user 2026-10-28): every plan on this screen is
 * a Desk plan. A Stays-only account reads a plain note instead — it cannot buy
 * a Desk plan by mistake (the server refuses it too, 409 DESK_IS_A_PURCHASE).
 * IS4 replaces the note with Stays billing. Desk accounts (or a session without
 * products) get the screen exactly as before.
 */
export const DashboardBilling: React.FC = () => {
  const { user } = useAuth();
  if (!deskInPlan(user)) {
    return <StaysOnlyBilling />;
  }

  return <DeskBilling />;
};

/* Independent Stays IS4 (C-5). A Stays-only account WITH a plan pays on the
   same pool as Desk, counted in places to stay: this page, with those words
   and without the Desk plan grid (its plan is chosen on Stays → Billing).
   Billing off, or a free preview with no plan: a note, nothing is charged. */
const StaysOnlyBilling: React.FC = () => {
  const [account, setAccount] = useState<'loading' | 'host' | 'preview' | 'off'>('loading');
  useEffect(() => {
    let live = true;
    getStaysBilling()
      .then(b => live && setAccount(b.enabled && b.account === 'host' ? 'host' : b.enabled ? 'preview' : 'off'))
      .catch(() => live && setAccount('off'));

    return () => {
      live = false;
    };
  }, []);
  if (account === 'loading') {
    return (
      <Wrap>
        <ListSkeleton rows={4} label="billing" />
      </Wrap>
    );
  }
  if (account === 'host') return <DeskBilling stays />;

  return (
    <Wrap>
      <Title>Billing</Title>
      <Note role="status" data-testid="billing-stays-only">
        {account === 'preview' ? (
          <>
            You are on the free preview — nothing is charged.{' '}
            <Link to="/dashboard/stays/billing">Choose a plan on Stays → Billing</Link>.
          </>
        ) : (
          'Billing for Lobbero Stays is not set up yet — nothing is charged.'
        )}
      </Note>
    </Wrap>
  );
};

const DeskBilling: React.FC<{ stays?: boolean }> = ({ stays = false }) => {
  // C-5: a Stays-only pool counts places to stay, not units.
  const W = stays
    ? { one: 'place to stay', many: 'places to stay', Many: 'Places to stay' }
    : { one: 'unit', many: 'units', Many: 'Units' };
  const word = (n: number) => (n === 1 ? W.one : W.many);
  const { permissions } = useAuth();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [sub, setSub] = useState<Sub | null>(null);
  const [buying, setBuying] = useState<number | null>(null);
  const [card, setCard] = useState<CardOnFile | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  // Counted from the archive itself, not only from the subscription row:
  // `sub.units_returned` is optional on the wire and reads as zero on an older
  // server — which hid the Restore door on a billing page that is supposed to
  // hold EVERY billing action, archive and revival included.
  const [returnedCount, setReturnedCount] = useState(0);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [addUnits, setAddUnits] = useState('1');
  const [buyOpen, setBuyOpen] = useState(false);
  const [returnOpen, setReturnOpen] = useState(false);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [planOpen, setPlanOpen] = useState(false);
  // Which interval the plan grid shows. Opens on the org's own interval.
  const [gridInterval, setGridInterval] = useState<BillingInterval>('month');
  const gridIntervalSet = useRef(false);
  const [keepBusy, setKeepBusy] = useState(false);
  const planRef = useRef(false);
  const buyUnitsRef = useRef(false);
  const [params] = useSearchParams();
  const canManageBilling = can(permissions, PERMISSIONS.BILLING_MANAGE);

  // ── PROMOTION CODES ──────────────────────────────────────────────────────
  //
  // The signup wizard has had a promo field since the beginning; this screen
  // never had one, so a code sent to an EXISTING customer had no surface to be
  // used on at all — and a promotion restricted to existing customers could not
  // be redeemed by anybody.
  //
  // Both calls are authoritative on the server: `preview` asks it what the code
  // is worth (it asks Stripe), `apply` re-judges every rule under a row lock
  // before it attaches anything. Nothing here computes a discount, and the
  // figures shown are the ones the server returned.
  const [promo, setPromo] = useState('');
  const [promoBusy, setPromoBusy] = useState(false);
  const [promoInfo, setPromoInfo] = useState<{
    valid: boolean;
    description?: string;
    message?: string;
    amounts?: { discount_cents: number; total_cents: number; currency: string } | null;
  } | null>(null);
  const [promoApplied, setPromoApplied] = useState<string | null>(null);

  const checkPromo = useCallback(async () => {
    const code = promo.trim();
    if (!code) return;
    setPromoBusy(true);
    setPromoInfo(null);
    try {
      const r = await axios.get(`${apiRoute}/subscriptions/promo/preview`, {
        params: { code },
      });
      setPromoInfo(r.data);
    } catch (e) {
      setPromoInfo({ valid: false, message: errMessage(e, 'Could not check that code.') });
    } finally {
      setPromoBusy(false);
    }
  }, [promo]);

  const applyPromo = useCallback(async () => {
    const code = promo.trim();
    if (!code || !sub?.stripe_subscription_id) return;
    setPromoBusy(true);
    try {
      const r = await axios.post(`${apiRoute}/subscriptions/apply-promo`, {
        subscription_id: sub.stripe_subscription_id,
        code,
      });
      setPromoApplied(r.data?.description || 'Discount applied');
      setPromoInfo(null);
      setPromo('');
      toast.success(`${r.data?.description || 'Discount'} applied`);
      await billing.reload();
    } catch (e) {
      // The server's own sentence — it names the rule that refused the code.
      setPromoInfo({ valid: false, message: errMessage(e, 'That promo code could not be applied.') });
    } finally {
      setPromoBusy(false);
    }
    // `billing` is declared below and only ever CALLED from here, so there is
    // no temporal-dead-zone hazard — the same pattern the cancel and resume
    // handlers above already use, and the reason it is not in the dependency
    // list. (No eslint-disable: this project does not load the react-hooks
    // plugin, and a disable comment for a rule that is not configured is itself
    // a build error under CRA.)
  }, [promo, sub]);
  const purchase = useUnitPurchase();
  const planChange = usePlanChange();

  // The plan grid opens on the interval the org is billed on — once, so a
  // reader who flips it is not flipped back by the next reload.
  useEffect(() => {
    const iv = sub?.plan?.billing_interval;
    if (!gridIntervalSet.current && iv) {
      gridIntervalSet.current = true;
      setGridInterval(iv === 'year' ? 'year' : 'month');
    }
  }, [sub]);

  // Declared before the effects that call it — every path that changes the
  // subscription re-reads it rather than patching local state, so what the
  // screen shows is always what the server holds.
  const reloadSub = async () => {
    try {
      const s = await axios
        .get(`${apiRoute}/dashboard/subscription`)
        .then(r => r.data);

      setSub(s);
      // A change can MOVE MONEY NOW (an annual unit increase, monthly ->
      // annual), and its invoice belongs in the history the moment it is paid.
      // This used to re-read only the subscription, so a customer who had just
      // paid $15.00 saw the old history until a reload (found driving the
      // billing screen as a real user, 2026-09-15). Secondary, like the first
      // load: a failure keeps the list it has.
      void axios
        .get(`${apiRoute}/subscriptions/invoices`)
        .then(r => {
          if (Array.isArray(r.data?.invoices)) setInvoices(r.data.invoices);
        })
        .catch(() => undefined);
      // The card too: paying through Stripe (a declined renewal, a 3-D Secure
      // charge) REPLACES the card on file, and this panel was read only on the
      // first load — so it went on naming the card that had just been swapped
      // out. Same rule as the invoices: a failed read keeps what it has.
      void axios
        .get(`${apiRoute}/subscriptions/payment-method`)
        .then(r => {
          if (r.data && 'card' in r.data) setCard(r.data.card ?? null);
        })
        .catch(() => undefined);
    } catch {
      /* A FAILED RE-READ IS NOT "NO SUBSCRIPTION".
         This is called after a purchase or a plan change, when the screen
         already holds a subscription — and `setSub(null)` on failure rendered
         "No active subscription" over an account that had just been charged.
         The previous value is left in place and the toast says the screen may
         be behind, which is true and far less alarming than the alternative. */
      toast.error(
        'We could not re-read your subscription just now. Reload to see the latest.'
      );
    }
  };

  // THE HOSTED FALLBACK'S RETURN LEG — POST /subscriptions/reconcile.
  //
  // Stripe redirects back to /dashboard/billing?checkout=success&session_id=…
  // and that session id is what ACTIVATES the subscription locally. This effect
  // used to read `checkout` and discard `session_id`, so on any box without a
  // webhook secret — which is every local dev box, where STRIPE_WEBHOOK_SECRET
  // is empty and the webhook route 503s every event before parsing it — the
  // hosted path was a silent no-op: the user paid, came back, saw "Subscription
  // updated", and nothing had changed. `onboarding-page.tsx:107-113` has always
  // done this correctly; this is the same call.
  //
  // The plan cards no longer use hosted Checkout, but the route is kept as the
  // explicit fallback for an org with nothing at Stripe yet, so its return leg
  // has to work.
  useEffect(() => {
    const c = params.get('checkout');
    const sessionId = params.get('session_id');
    if (c === 'cancel') {
      toast('Checkout canceled');

      return;
    }
    if (c !== 'success') return;

    reconcileCheckout(sessionId)
      .then(() => {
        toast.success('Subscription updated');

        return reloadSub();
      })
      .catch(() => {
        // The webhook reconciles too where one is configured, so this is not
        // fatal — but it is not silent either: an unreconciled return means the
        // screen below is showing the OLD subscription.
        toast.error(
          'We could not confirm that change with our payment provider. ' +
            'Refresh in a moment, or contact support if it does not appear.'
        );
      });
  }, []);

  /* BOTH READS USED TO SWALLOW FAILURE — `.catch(() => [])` and
     `.catch(() => null)` — so a 403, a 500 and a dropped connection rendered
     the same screen as a genuine "no plans, no subscription": the money screen,
     reporting nothing wrong, on an account that has both. */
  const loadBilling = useCallback(async () => {
    const [p, s] = await Promise.all([
      fetchAllItems('dashboard/plans'),
      axios.get(`${apiRoute}/dashboard/subscription`).then(r => r.data),
    ]);

    setPlans(Array.isArray(p) ? (p as Plan[]) : []);
    setSub(s);

    /* The card and the invoices are SECONDARY: this screen is usable without
       either, and neither should be able to take down the plan grid. So they
       are fetched after the two that matter and their failures are swallowed
       into an absent state, which each section renders as "not available"
       rather than as an empty truth. */
    void axios
      .get(`${apiRoute}/subscriptions/payment-method`)
      .then(r => setCard(r.data?.card ?? null))
      .catch(() => setCard(null));
    void axios
      .get(`${apiRoute}/subscriptions/invoices`)
      .then(r => setInvoices(Array.isArray(r.data?.invoices) ? r.data.invoices : []))
      .catch(() => setInvoices([]));
    // Secondary for the same reason as the card and the invoices: the archive
    // count opens the Restore door, and its failure must degrade to a hidden
    // door rather than take the money screen down.
    void getReturnedUnits()
      .then(r =>
        setReturnedCount(
          Number.isInteger(r?.count) ? r.count : (r?.units || []).length
        )
      )
      .catch(() => setReturnedCount(0));

    return true;
  }, []);

  /**
   * CANCELLING — the promise the signup screen makes.
   *
   * "Nothing is charged for 30 days. Cancel anytime before the trial ends." was
   * on the screen that takes the card, against a product with no cancel control
   * and no route behind one. At period end rather than immediately: the payer
   * keeps what they have already paid for.
   */
  const cancelSubscription = async () => {
    if (cancelBusy) return;
    setCancelBusy(true);
    try {
      const r = await axios.post(`${apiRoute}/subscriptions/cancel`);
      // Cancelling supersedes a scheduled switch to monthly (Stripe refuses a
      // cancel on a scheduled subscription, so the server releases it first).
      toast.success(
        r.data && r.data.scheduled_change_cleared
          ? 'Your subscription will end at the close of this period. The scheduled switch to monthly billing was cancelled with it.'
          : 'Your subscription will end at the close of this period.'
      );
      setCancelOpen(false);
      await billing.reload();
    } catch (e) {
      toast.error(
        compedRefusal(e) ||
          'We could not cancel the subscription. Please try again.'
      );
    } finally {
      setCancelBusy(false);
    }
  };

  const resumeSubscription = async () => {
    if (cancelBusy) return;
    setCancelBusy(true);
    try {
      await axios.post(`${apiRoute}/subscriptions/resume`);
      toast.success('Your subscription will continue.');
      await billing.reload();
    } catch (e) {
      // The one refusal with a different way forward: the period already
      // closed, so the button that can help is "Choose plan", not "try again".
      const code =
        axios.isAxiosError(e) && e.response?.data?.code
          ? String(e.response.data.code)
          : '';
      toast.error(
        compedRefusal(e) ||
          (code === 'SUBSCRIPTION_ALREADY_CANCELED'
            ? 'This subscription has already ended. Choose a plan below to start a new one.'
            : 'We could not resume the subscription. Please try again.')
      );
      if (code === 'SUBSCRIPTION_ALREADY_CANCELED') {
        await billing.reload();
      }
    } finally {
      setCancelBusy(false);
    }
  };

  const billing = useResource<boolean>(loadBilling, {
    fallbackMessage: 'We could not load your plan and billing details.',
  });
  const loading = billing.isLoading;

  // CHANGE PLAN — in the shared modal, never a redirect.
  //
  // `buy()` used to POST /subscriptions/checkout and set
  // `window.location.href = data.url`. Hosted Checkout in subscription mode
  // CREATES a subscription and nothing cancelled the old one, so a plan change
  // by an already-invoiced customer billed them twice for the same month: the
  // old period was paid, a bare cancel issues no credit, and the new
  // subscription started its own billing. The server now updates the EXISTING
  // subscription onto the new price.
  //
  // THE CONTACT-SALES GUARD IS NOT DECORATIVE. Custom is a sales lead and never
  // a payment path (owner decision D-17): it has no Stripe price, so there is
  // nothing to move a subscription onto. The card below renders a mailto link
  // rather than a button, and this refuses anyway — a screen where the only
  // thing standing between a sales conversation and a payment modal is which
  // JSX branch rendered is one refactor away from being wrong.
  const openPlanChange = async (p: Plan) => {
    if (p.contact_sales) {
      toast(
        'The Custom plan is arranged with our sales team — get in touch and ' +
          'they will set it up for you.'
      );

      return;
    }
    // THE SUBSCRIPTION HAS ENDED: there is nothing to change, so choosing a
    // plan starts a new one through Stripe's hosted checkout. The server
    // refuses an org that still has a live subscription and never re-grants a
    // trial to an org that already had one.
    if (!sub) {
      if (planRef.current) {
        return;
      }
      planRef.current = true;
      setBuying(p.id);
      try {
        const { url } = await startCheckout(p.id);
        window.location.href = url;
      } catch (e) {
        toast.error(errMessage(e, 'We could not start checkout. Please try again.'));
      } finally {
        planRef.current = false;
        setBuying(null);
      }

      return;
    }
    // Re-entry guard, armed BEFORE any validation return: arming it after would
    // still let a double-fire raise two toasts and send two quotes.
    if (planRef.current) {
      return;
    }

    planRef.current = true;
    setBuying(p.id);
    try {
      setPlanOpen(true);
      await planChange.quote(p.id);
    } finally {
      planRef.current = false;
      setBuying(null);
    }
  };

  const afterPlanChange = async (result: PlanChangeResult) => {
    toast.success(
      result.mode === 'scheduled'
        ? `You will switch to ${result.scheduled_plan_name} (monthly) on ${onlyDate(
            result.effective_at
          )}. Your annual plan continues until then.`
        : result.charged_now_cents
        ? `You are now on ${result.plan_name}. ${money(
            result.charged_now_cents,
            sub?.plan?.currency || 'usd'
          )} was charged.`
        : `You are now on ${result.plan_name}`
    );
    setPlanOpen(false);
    planChange.reset();
    await reloadSub();
  };

  const closePlanChange = () => {
    setPlanOpen(false);
    planChange.reset();
  };

  // "KEEP ANNUAL" — take back a scheduled switch to monthly before it happens.
  const keepAnnual = async () => {
    if (keepBusy) return;
    setKeepBusy(true);
    try {
      await cancelScheduledChange();
      toast.success('You are staying on annual billing.');
      await reloadSub();
    } catch (e) {
      toast.error(
        errMessage(e, 'We could not cancel the scheduled switch. Please try again.')
      );
    } finally {
      setKeepBusy(false);
    }
  };

  // The price as billed: per unit per MONTH, or per unit per YEAR. An annual
  // price is never shown as if it were monthly.
  const price = (p: Plan) => {
    if (p.contact_sales) return 'Custom pricing';

    return `$${(p.price_cents / 100).toFixed(2)}/unit/${
      p.billing_interval === 'year' ? 'yr' : 'mo'
    }`;
  };
  // For an annual row only: the monthly equivalent, labelled as one.
  const effective = (p: Plan) =>
    !p.contact_sales && p.billing_interval === 'year' && p.effective_monthly_cents
      ? `$${(p.effective_monthly_cents / 100).toFixed(2)}/unit/mo, billed annually`
      : null;
  const hasAnnual = plans.some(p => p.billing_interval === 'year');
  const shownPlans = plans.filter(
    p => p.contact_sales || !hasAnnual || p.billing_interval === gridInterval
  );

  // Units are prepaid. "Buy more units" used to POST straight from this row: no
  // quote, no confirmation, and the only number on screen was a per-unit rate.
  // It now opens the shared card modal, which states what the org will actually
  // pay before anything is committed — the quote comes from the server
  // (GET /subscriptions/units/preview), never from arithmetic done here.
  //
  // The modal collects a CARD only when the server asks for one AND says what
  // adding one would do. Lobbero keeps no card: what is collected is a Stripe
  // SetupIntent, and the id it produces is re-verified server-side before a
  // single unit is granted.
  const openBuyUnits = async () => {
    // Re-entry guard, same shape as `checkoutRef` above. Armed BEFORE the
    // validation return on purpose: arming it after would still let a
    // double-fire raise two error toasts.
    if (buyUnitsRef.current) {
      return;
    }

    buyUnitsRef.current = true;
    try {
      const add = parseInt(addUnits, 10);
      if (!Number.isInteger(add) || add < 1) {
        toast.error(`Enter how many ${W.many} to add`);

        return;
      }
      const current = sub?.units_allowed ?? sub?.units_used ?? 0;
      setBuyOpen(true);
      await purchase.quote(current + add);
    } finally {
      buyUnitsRef.current = false;
    }
  };

  // Shared tail of both commit paths. Anything that throws before this — a
  // declined card, a refused proof, a Stripe failure — leaves the allowance
  // where it was, which is why the toast and the reload are here and not in the
  // hook: they are what "it actually happened" looks like.
  const afterBuy = async (result: {
    units_allowed: number;
    subscription_created?: boolean;
    charged_now_cents?: number;
  }) => {
    const current = sub?.units_allowed ?? sub?.units_used ?? 0;
    const added = result.units_allowed - current;
    toast.success(
      result.subscription_created
        ? 'Card saved and your subscription is active'
        : `Added ${added} ${word(added)}${
            result.charged_now_cents
              ? ` — ${money(result.charged_now_cents, sub?.plan?.currency || 'usd')} charged`
              : ''
          }`
    );
    setAddUnits('1');
    setBuyOpen(false);
    purchase.reset();
    await reloadSub();
    // The DETECTION moment: capacity just went up and the org holds previously
    // returned units — offer them back before the new slots get spent on fresh
    // ones. `units_returned` is the PRE-purchase value on purpose (buying
    // changes capacity, never the archived set), so the stale read is exact.
    if ((sub?.units_returned ?? 0) > 0) {
      setRestoreOpen(true);
    }
  };

  const closeBuy = () => {
    setBuyOpen(false);
    purchase.reset();
  };

  const preview = purchase.preview;
  const planPreview = planChange.preview;
  const currentPlanId = sub?.plan_id ?? sub?.plan?.id;
  // Read, never derived. `units_remaining` is clamped at zero in SQL, so an org
  // past its cap reports "0 available" — exactly what an org that is precisely
  // full reports. This number is the only thing that tells the two apart, and
  // it is the server's, not arithmetic done on this screen.
  const unitsOver = sub?.units_over ?? 0;
  // COMPLIMENTARY: said at the top, and every control whose request the server
  // would refuse with COMPED_ACCOUNT is not rendered — buy units, return units,
  // change plan, promo code, card, cancel. What stays is what is true of the
  // account: its plan, its units, their use, and restoring archived units into
  // room it already has (a units route, not a billing one).
  const comped = !!sub?.comped;
  // IS6-D2: the bundle has no plan grid; its plan changes with its products.
  const bundle = sub?.plan?.product_line === 'bundle';

  /* First load: placeholder rows where the plan, card and invoices will be.
     Every later reload (after a purchase, a plan change, a card update) keeps
     the screen and dims it — useResource's isRefreshing is that signal. */
  return (
    <Wrap>
      <StateSwitch state={loading ? 'loading' : 'ready'} minHeight={320}>
      {loading ? (
        <ListSkeleton rows={4} label="billing" />
      ) : (
      <Staleable stale={billing.isRefreshing}>
      <Title>Billing</Title>

      <ProductsCard onChanged={billing.reload} />

      {/* Said out loud, at the top of the money screen. An account that HAS a
          plan and a subscription used to render as though it had neither. */}
      {billing.error && (
        <LoadError message={billing.error} onRetry={billing.reload} />
      )}

      {/* Lobbero IQ month-to-date usage (owner-only). Mounted only when the
          subscription includes IQ: the row used to probe GET /iq/usage for
          every owner and hide itself on the 403, which put a failed request
          on every billing view of a Desk plan. It still self-suppresses on
          any error. */}
      {canManageBilling && sub?.iq_enabled ? <IqBillingRow /> : null}

      {sub ? (
        <Current>
          <CurrentLabel>Current plan</CurrentLabel>
          <CurrentName>
            {sub.plan?.name || 'Active plan'}
            {sub.plan && !sub.plan.contact_sales && !comped
              ? ` · ${intervalWord(sub.plan.billing_interval)}`
              : ''}
          </CurrentName>
          <CurrentMeta>
            {sub.unit_quantity ?? 0} units ·{' '}
            {comped ? 'Complimentary — no charge' : describeSubStatus(sub.status)}
          </CurrentMeta>
          {/* NOT "$2.86/unit/mo · renews …". That line is a price and a
              renewal date, and a complimentary account has neither. */}
          {comped ? (
            <CompedNote data-testid="billing-comped">
              <b>Complimentary plan — no charge.</b> There is nothing to pay,
              no card is needed and nothing renews. Units and plan on this
              account are set by our team: to change them, or to move onto a
              paid subscription, contact us at{' '}
              <a href="mailto:hello@lobbero.com">hello@lobbero.com</a>.
            </CompedNote>
          ) : sub.plan && !sub.plan.contact_sales && (
            <CurrentMeta>
              {price(sub.plan)}
              {sub.status === 'trialing' && sub.trial_end
                ? ` · first charge ${onlyDate(sub.trial_end)}`
                : sub.current_period_end && !sub.cancel_at_period_end
                ? ` · renews ${onlyDate(sub.current_period_end)}`
                : ''}
            </CurrentMeta>
          )}
          {/* GRANDFATHERED (migration 105): the org keeps a price nobody new
              can choose. Said out loud, because changing plan or billing
              period moves it to current pricing. */}
          {sub.plan?.legacy && !comped && (
            <CurrentMeta>
              You keep this rate — it is no longer offered to new customers.
              Changing plan or billing period moves you to current pricing.
            </CurrentMeta>
          )}
          {sub.scheduled_plan && sub.scheduled_change_at && (
            <ScheduleNote role="status">
              {/* ONE text item: the note is a flex row, and bare text nodes
                  around the <b> each became their own flex item, so the gap
                  opened spaces around the date ("on  15/09/2027  ."). */}
              <span>
                Switching to {sub.scheduled_plan.name} ·{' '}
                {intervalWord(sub.scheduled_plan.billing_interval)} on{' '}
                <b>{onlyDate(sub.scheduled_change_at)}</b>. Your annual plan
                continues until then.
              </span>
              {canManageBilling && (
                <AppButton
                  variant="secondary"
                  size="compact"
                  loading={keepBusy}
                  loadingLabel="Keeping annual"
                  onClick={keepAnnual}
                >
                  Keep annual billing
                </AppButton>
              )}
            </ScheduleNote>
          )}
        </Current>
      ) : (
        <Current>
          <CurrentLabel>No active subscription</CurrentLabel>
        </Current>
      )}

      {/* THE CARD ON FILE. It could be entered only inside a purchase and was
          never shown again — so nobody could check which card was on file, see
          that it was about to expire, or replace it before a renewal failed. */}
      {sub && !comped && (
        <Current>
          <CurrentLabel>Card on file</CurrentLabel>
          {card ? (
            <>
              <CurrentName>
                {card.brand.toUpperCase()} ending {card.last4}
              </CurrentName>
              <CurrentMeta>
                Expires{' '}
                {`${String(card.exp_month).padStart(2, '0')}/${card.exp_year}`}
              </CurrentMeta>
            </>
          ) : (
            <CurrentMeta>
              No card saved yet. One is asked for the first time a charge needs
              it.
            </CurrentMeta>
          )}
        </Current>
      )}

      {/* ── A PROMOTION CODE ───────────────────────────────────────────────
          Shown only to somebody who may manage billing, and only when there is
          a subscription to discount. The figures come from the server, which
          asked Stripe; nothing here multiplies a price by a percentage. */}
      {sub && canManageBilling && !comped && (
        <Current>
          <CurrentLabel>Promo code</CurrentLabel>
          {/* WHAT THEY HAVE, EVERY TIME THEY LOOK — not only in the moment Apply
              was pressed. A code applied at signup used to be invisible here. */}
          {sub.promotion ? (
            <CurrentMeta role="status" id="billing-promo-current">
              Current promotion: <b>{sub.promotion.description}</b> ({sub.promotion.code})
              {sub.promotion.ends_at ? `, until ${onlyDate(sub.promotion.ends_at)}` : ''}.
              {promoApplied ? ' Just applied to this subscription — it will show on your next invoice.' : ''}
            </CurrentMeta>
          ) : promoApplied ? (
            <CurrentMeta role="status">
              {promoApplied} — applied to this subscription. It will show on your
              next invoice.
            </CurrentMeta>
          ) : null}
          <PromoRow
            onSubmit={e => {
              e.preventDefault();
              if (promoInfo?.valid) applyPromo();
              else checkPromo();
            }}
          >
            <PromoInput
              id="billing-promo"
              value={promo}
              onChange={e => {
                setPromo(e.target.value);
                setPromoInfo(null);
              }}
              placeholder="Enter a code"
              autoCapitalize="characters"
              autoComplete="off"
              aria-label="Promo code"
            />
            <AppButton
              type="submit"
              variant="secondary"
              size="compact"
              loading={promoBusy}
              loadingLabel={promoInfo?.valid ? 'Applying' : 'Checking'}
              disabled={!promo.trim()}
            >
              {promoInfo?.valid ? 'Apply' : 'Check'}
            </AppButton>
          </PromoRow>
          {promoInfo?.valid ? (
            <CurrentMeta role="status">
              <b>{promoInfo.description}</b>
              {promoInfo.amounts
                ? ` — takes ${money(
                    promoInfo.amounts.discount_cents,
                    sub.plan?.currency || 'usd'
                  )} off your next ${intervalWord(
                    sub.plan?.billing_interval
                  ).toLowerCase()} payment, bringing it to ${money(
                    promoInfo.amounts.total_cents,
                    sub.plan?.currency || 'usd'
                  )}.`
                : '.'}{' '}
              Press Apply to use it.
            </CurrentMeta>
          ) : null}
          {promoInfo && !promoInfo.valid ? (
            <PromoError role="alert">
              {promoInfo.message || 'That promo code is not valid.'}
            </PromoError>
          ) : null}
        </Current>
      )}

      {/* CANCELLING, AND UNDOING A CANCELLATION. */}
      {sub && canManageBilling && !comped && (
        <Current>
          {sub.cancel_at_period_end ? (
            <CancelRow>
              <CancelText>
                <CurrentLabel>Cancelling</CurrentLabel>
                <CurrentMeta>
                  Your subscription ends on {onlyDate(sub.current_period_end)}.
                  Until then nothing changes.
                </CurrentMeta>
              </CancelText>
              <AppButton
                variant="secondary"
                size="compact"
                disabled={cancelBusy}
                onClick={resumeSubscription}
              >
                Resume subscription
              </AppButton>
            </CancelRow>
          ) : (
            <CancelRow>
              <CancelText>
                <CurrentLabel>Cancel</CurrentLabel>
                <CurrentMeta>
                  Ends at the close of the period you have paid for — you keep
                  access until then.
                </CurrentMeta>
              </CancelText>
              <AppButton
                variant="ghost"
                size="compact"
                disabled={cancelBusy}
                onClick={() => setCancelOpen(true)}
              >
                Cancel subscription
              </AppButton>
            </CancelRow>
          )}
        </Current>
      )}

      {/* BILLING HISTORY. A grep for "invoice" across the frontend used to
          return only comments: a customer could see what they were about to pay
          and never what they had paid. The PDF is Stripe's own hosted document
          rather than something re-rendered here — it is the authoritative
          receipt and carries the tax fields this product does not model. */}
      {sub && invoices.length > 0 && (
        <Current>
          <CurrentLabel>Billing history</CurrentLabel>
          <InvoiceList>
            {invoices.map(invoice => (
              <InvoiceRow key={invoice.id}>
                <span>{onlyDate(invoice.created)}</span>
                <span>
                  {money(
                    invoice.amount_paid || invoice.amount_due,
                    invoice.currency
                  )}
                </span>
                <InvoiceStatus>{invoice.status}</InvoiceStatus>
                {invoice.hosted_invoice_url ? (
                  <a
                    href={invoice.hosted_invoice_url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {/* The same Stripe page is a receipt once paid and the
                        place to PAY while open — it used to say "View
                        receipt" on a declined, unpaid invoice (found driving
                        billing as a past-due customer, 2026-09-15). */}
                    {invoice.status === 'open' ? 'Pay invoice' : 'View receipt'}
                  </a>
                ) : (
                  <span />
                )}
              </InvoiceRow>
            ))}
          </InvoiceList>
        </Current>
      )}

      <ConfirmDialog
        open={cancelOpen}
        title="Cancel your subscription?"
        message={`Your subscription stays active until ${
          // A billing date the server has not synced yet must read as a
          // sentence, not as "active until —". The em-dash placeholder is for
          // tables; a dialog asking for a decision gets words.
          sub?.current_period_end
            ? onlyDate(sub.current_period_end)
            : 'the end of the current billing period'
        }, then ends. ${stays ? 'Your places to stay keep working until that date, and you can resume at any point before it. When it ends, guests can no longer check in online — nothing is deleted, and everything comes back if you resubscribe.' : 'Your desks keep working until that date, and you can resume at any point before it. When it ends, your units go offline — nothing is deleted: every unit keeps its number, its building and its history, and can be restored if you resubscribe.'}`}
        primaryLabel={cancelBusy ? 'Cancelling…' : 'Cancel subscription'}
        primaryVariant="destructive"
        dismissLabel="Keep my subscription"
        onPrimary={cancelSubscription}
        onDismiss={() => setCancelOpen(false)}
      />

      {sub && (
        <Units>
          <CurrentLabel>
            {comped ? `${W.Many} — complimentary` : `${W.Many} — prepaid`}
          </CurrentLabel>
          <UnitsHint>
            {stays
              ? comped
                ? 'Each apartment or whole place counts as one. Contact us to add more.'
                : 'Each apartment or whole place counts as one. Buy more to add places.'
              : comped
              ? 'A building is a group of units. You can create up to the units on this account — contact us to add more.'
              : 'A building is a group of units. You can create up to the units you own — buy more to add capacity.'}
          </UnitsHint>
          {unitsOver > 0 && (
            <OverCap role="status">
              You are using <b>{unitsOver}</b> more {word(unitsOver)} than you have purchased.
              {comped
                ? ` Contact us at hello@lobbero.com to add ${W.many} to this complimentary plan.`
                : canManageBilling
                ? ` Buy more ${W.many} below to cover the difference.`
                : ` Ask an account admin to add more ${W.many}.`}
            </OverCap>
          )}
          {/* Figures, buy and return share one centred line; on a phone each
              group wraps onto a centred line of its own. */}
          <UnitsBar>
            <UnitsStats>
              <UnitsStat>
                <b>{sub.units_used ?? 0}</b> in use
              </UnitsStat>
              <UnitsStat>
                <b>{sub.units_allowed ?? '∞'}</b> {comped ? 'included' : 'bought'}
              </UnitsStat>
              <UnitsStat>
                <b>{sub.units_remaining ?? '∞'}</b> available
              </UnitsStat>
            </UnitsStats>
            {canManageBilling && !comped && (
              <BuyRow
                onSubmit={e => {
                  e.preventDefault();
                  openBuyUnits();
                }}
              >
                <BuyInput
                  type="number"
                  min={1}
                  value={addUnits}
                  onChange={e => setAddUnits(e.target.value)}
                  placeholder={`How many ${W.many}?`}
                  aria-label={`${W.Many} to add`}
                />
                {/* type="submit" only — the form's onSubmit above is the single
                    path into buyMore. An onClick here as well fired it twice. */}
                <ChooseBtn
                  type="submit"
                  loading={purchase.quoting}
                  loadingLabel="Pricing"
                >
                  Buy more units
                </ChooseBtn>
              </BuyRow>
            )}
            {canManageBilling && (
              <UnitsActions>
                {(sub.units_allowed ?? 0) > 1 && !comped && (
                  <AppButton
                    variant="ghost"
                    onClick={() => setReturnOpen(true)}
                  >
                    Return units…
                  </AppButton>
                )}
                {(() => {
                  // The archive list is the truth; the sub field is the fast
                  // path. Either one being positive opens the door.
                  const returned = Math.max(
                    sub.units_returned ?? 0,
                    returnedCount
                  );

                  return returned > 0 ? (
                    <AppButton
                      variant="secondary"
                      onClick={() => setRestoreOpen(true)}
                    >
                      Restore {returned} returned unit
                      {returned === 1 ? '' : 's'}…
                    </AppButton>
                  ) : null;
                })()}
              </UnitsActions>
            )}
          </UnitsBar>
        </Units>
      )}

      <ReturnUnitsDialog
        open={returnOpen}
        unitsAllowed={sub?.units_allowed ?? 0}
        onDismiss={() => setReturnOpen(false)}
        onReturned={async () => {
          setReturnOpen(false);
          await reloadSub();
        }}
      />

      <RestoreUnitsDialog
        open={restoreOpen}
        remaining={sub?.units_remaining ?? 0}
        onDismiss={() => setRestoreOpen(false)}
        onRestored={async () => {
          setRestoreOpen(false);
          await reloadSub();
        }}
      />

      {/* THE PLAN GRID IS A PURCHASE SURFACE — every card is a price and a
          Choose button — so a complimentary account does not get one. */}
      {/* A Stays-only account's plan is a Stays plan (Stays → Billing); the
          grid lists Desk plans only. */}
      {hasAnnual && !comped && !stays && !bundle && (
        <PlansHeader>
          <CurrentLabel>Plans</CurrentLabel>
          <IntervalToggle
            value={gridInterval}
            onChange={setGridInterval}
            savingsPercent={annualBadgePercent(plans)}
          />
        </PlansHeader>
      )}
      {!comped && !stays && !bundle && (
        <PlansGrid>
          {shownPlans.map(p => {
            const isCurrent = p.id === currentPlanId;
            const isScheduled = !!sub && p.id === sub.scheduled_plan_id;
            const eff = effective(p);

            return (
              <PlanCard key={p.id} current={isCurrent}>
                <PlanName>{p.name}</PlanName>
                <PlanPrice>{price(p)}</PlanPrice>
                {eff && <PlanEffective>{eff}</PlanEffective>}
                <PlanDesc>{p.description}</PlanDesc>
                <PlanLimit>
                  {p.contact_sales
                    ? 'For 5+ buildings'
                    : `${p.iq_enabled ? 'Includes Lobbero IQ' : 'Per unit'} · billed ${
                        p.billing_interval === 'year' ? 'annually' : 'monthly'
                      }`}
                </PlanLimit>
                {isCurrent ? (
                  <CurrentBadge>Current plan</CurrentBadge>
                ) : isScheduled ? (
                  <CurrentBadge>Switching on {onlyDate(sub?.scheduled_change_at)}</CurrentBadge>
                ) : p.contact_sales ? (
                  /* NOT A PAYMENT PATH (D-17). Custom is a sales lead: it is
                     `contact_sales` with no Stripe price, so there is nothing to
                     move a subscription onto. It stays a contact link and never
                     opens the card modal — and openPlanChange refuses it too, so
                     the JSX branch is not the only thing enforcing that. */
                  <ContactLink href="mailto:hello@lobbero.com">
                    Contact sales
                  </ContactLink>
                ) : canManageBilling ? (
                  <ChooseBtn
                    loading={buying === p.id}
                    loadingLabel={`Choosing ${p.name}`}
                    onClick={() => openPlanChange(p)}
                  >
                    {sub ? 'Choose plan' : 'Subscribe'}
                  </ChooseBtn>
                ) : (
                  <span />
                )}
              </PlanCard>
            );
          })}
        </PlansGrid>
      )}

      {!canManageBilling && !comped && (
        <Note>Only account admins can change the subscription.</Note>
      )}

      {/* The shared card surface. `collectCard` is TRUE only when the server
          refused for want of a card AND told us what adding one would do — a
          refusal with no `recovery` (or from an older server) keeps the card
          form shut and the purchase blocked, because a card form with no
          SetupIntent behind it collects a card that goes nowhere. */}
      <StripeCardModal
        open={buyOpen}
        title={purchase.collectCard ? 'Add a card' : `Add ${W.many}`}
        description={
          preview
            ? purchase.collectCard
              ? 'Your card is held by Stripe, never by Lobbero. Nothing is charged until you confirm below.'
              : `${W.Many} are what you are billed for. ${
                  preview.plan_name || 'Your plan'
                } is charged per ${W.one} per ${preview.interval}.${
                  preview.mode === 'charge_now'
                    ? ' On annual billing the rest of this year is charged to your card when you confirm.'
                    : ''
                }`
            : 'Getting a quote from Stripe…'
        }
        onDismiss={closeBuy}
        collectCard={purchase.collectCard}
        prepareIntent={purchase.prepareIntent}
        onConfirmed={async result => {
          await afterBuy(await purchase.confirmed(result));
        }}
        onSubmit={async () => {
          await afterBuy(await purchase.submit());
        }}
        summaryRows={purchase.summaryRows}
        notice={
          purchase.refusal || purchase.paymentLink ? (
            <BuyNotice role="alert">
              {purchase.refusal}
              {purchase.collectCard && preview && preview.recovery ? (
                <>
                  {' '}
                  {/* Keyed on the CHARGE, not on the trial flag. An active
                      subscription with no card on file preserves no trial and
                      is charged nothing today either — telling that org billing
                      "starts straight away" would be a third wrong number on a
                      screen this package exists to stop producing them. */}
                  {preview.recovery.first_charge_cents > 0
                    ? preview.interval === 'year'
                      ? 'Your card is charged today, and at every annual renewal from now on.'
                      : 'Your card is charged today, and every month from now on.'
                    : preview.recovery.trial_preserved
                    ? 'Your trial is unchanged — adding a card does not start billing early.'
                    : 'Nothing is charged today; the change appears on your next invoice.'}
                </>
              ) : null}
              {/* A declined or 3-D Secure charge changed NOTHING; Stripe's
                  hosted page completes it, and the webhook then applies it. */}
              {purchase.paymentLink && (
                <>
                  {' '}
                  <a href={purchase.paymentLink} target="_blank" rel="noreferrer">
                    Complete the payment on Stripe&rsquo;s secure page
                  </a>
                  .
                </>
              )}
            </BuyNotice>
          ) : null
        }
        submitDisabled={purchase.blocked || purchase.quoting}
        /* While the quote is in flight the button says PRICING, not "Confirm".
           It is disabled either way, but a disabled button labelled "Confirm"
           is a button that offers to commit a number nobody has been shown —
           and it is indistinguishable, to a person or to a test, from the real
           one that lands a moment later. */
        submitLabel={
          purchase.quoting
            ? 'Pricing…'
            : preview
            ? purchase.collectCard
              ? `Save card and add ${preview.delta} ${word(preview.delta)}`
              : preview.mode === 'charge_now' && preview.amount_due_now_cents != null
              ? `Pay ${money(preview.amount_due_now_cents, preview.currency)} and add ${
                  preview.delta
                } ${word(preview.delta)}`
              : `Confirm ${preview.delta} ${word(preview.delta)}`
            : 'Confirm'
        }
        busyLabel="Adding…"
        cancelLabel="Cancel"
        onCancel={closeBuy}
        /* A QUANTITY_CHANGED refusal is already on screen in the notice above,
           over a fresh quote; the form's own error line would say it twice. */
        onError={purchase.handleCommitError}
        fallbackErrorMessage={`Could not add ${W.many}. Please try again.`}
      />

      {/* CHANGE PLAN. Same modal, same four steps, a different Stripe field:
          a unit purchase changes the QUANTITY on the subscription, a plan
          change changes its PRICE. The server updates the subscription the org
          already has — it never creates a second one, which is what made the
          hosted-checkout redirect a double bill. */}
      <StripeCardModal
        open={planOpen}
        title={
          planChange.collectCard
            ? 'Add a card to change plan'
            : planPreview && planPreview.plan_name
            ? `Change to ${planPreview.plan_name} · ${intervalWord(
                planPreview.interval_after || planPreview.interval
              )}`
            : 'Change plan'
        }
        description={
          planPreview
            ? planChange.collectCard
              ? 'Your card is held by Stripe, never by Lobbero. Nothing is charged until you confirm below.'
              : planPreview.mode === 'trial'
              ? 'Your trial is unchanged — the new rate begins when it ends.'
              : planPreview.mode === 'charge_now'
              ? 'Your existing subscription moves to this plan. The difference for the rest of the billing period is charged to your card when you confirm.'
              : planPreview.mode === 'scheduled'
              ? `Nothing changes until ${onlyDate(
                  planPreview.effective_at
                )}. You keep the annual plan you paid for until then, and monthly billing starts after it.`
              : planPreview.mode === 'refuse'
              ? // A REFUSED change used to fall through to the proration
                // sentence — "Your existing subscription moves to this plan…"
                // above a disabled button and the reason it cannot (found
                // driving billing as a real user while cancelling, 2026-09-15).
                'This change cannot be made right now — the reason is below. Nothing has changed.'
              : 'Your existing subscription moves to this plan. Nothing is charged today; the adjustment appears on your next invoice.'
            : 'Getting a quote from Stripe…'
        }
        onDismiss={closePlanChange}
        collectCard={planChange.collectCard}
        prepareIntent={planChange.prepareIntent}
        onConfirmed={async result => {
          await afterPlanChange(await planChange.confirmed(result));
        }}
        onSubmit={async () => {
          await afterPlanChange(await planChange.submit());
        }}
        summaryRows={planChange.summaryRows}
        notice={
          planChange.refusal || planChange.paymentLink ? (
            <BuyNotice role="alert">
              {planChange.refusal}
              {planChange.paymentLink && (
                <>
                  {' '}
                  <a href={planChange.paymentLink} target="_blank" rel="noreferrer">
                    Complete the payment on Stripe&rsquo;s secure page
                  </a>
                  .
                </>
              )}
            </BuyNotice>
          ) : planPreview &&
            (planPreview.iq_change !== 'none' ||
              (planPreview.discounts_removed || []).length > 0) ? (
            /* Lobbero IQ is switched by subscriptions.iq_enabled, which this
               change writes. A downgrade must not remove a feature the org is
               using without saying so on the screen that does it — and a
               promotion annual billing does not carry is named the same way. */
            <BuyNotice role="status">
              {planPreview.iq_change === 'lost'
                ? 'Lobbero IQ is included in your current plan and is NOT included in this one. It will be switched off for your whole organisation. '
                : planPreview.iq_change === 'gained'
                ? 'Lobbero IQ is included in this plan and will be switched on for your organisation. '
                : ''}
              {(planPreview.discounts_removed || []).length > 0 &&
                `Your promotion (${(planPreview.discounts_removed || [])
                  .map(d => d.description)
                  .join(', ')}) does not apply to ${
                  planPreview.plan_name
                    ? `the ${planPreview.plan_name} plan${
                        planPreview.interval_after
                          ? ` billed ${planPreview.interval_after === 'year' ? 'annually' : 'monthly'}`
                          : ''
                      }`
                    : 'this plan'
                } and ends ${
                  planPreview.mode === 'scheduled'
                    ? `when the switch takes effect on ${onlyDate(planPreview.effective_at)}`
                    : 'with this change'
                }.`}
            </BuyNotice>
          ) : null
        }
        submitDisabled={planChange.blocked || planChange.quoting}
        submitLabel={
          planChange.quoting
            ? 'Pricing…'
            : planPreview && planPreview.plan_name
            ? planChange.collectCard
              ? `Save card and switch to ${planPreview.plan_name}`
              : planPreview.mode === 'scheduled'
              ? `Switch on ${onlyDate(planPreview.effective_at)}`
              : planPreview.mode === 'charge_now' &&
                planPreview.amount_due_now_cents != null
              ? `Pay ${money(
                  planPreview.amount_due_now_cents,
                  planPreview.currency
                )} and switch`
              : `Switch to ${planPreview.plan_name}`
            : 'Confirm'
        }
        busyLabel="Changing…"
        cancelLabel="Cancel"
        onCancel={closePlanChange}
        fallbackErrorMessage="Could not change your plan. Please try again."
      />
      </Staleable>
      )}
      </StateSwitch>
    </Wrap>
  );
};

const Wrap = styled.div`
  padding: ${spacing.lg};
`;

const Title = styled.h2`
  margin: 0 0 ${spacing.lg};
  font-size: ${typography.size.xl}px;
  font-weight: 700;
  color: ${palette.ink};
`;

const Current = styled.section`
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  box-shadow: ${shadow.sm};
  padding: ${spacing.lg};
  margin-bottom: ${spacing.lg};
`;

const CurrentLabel = styled.div`
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-size: 12px;
  font-weight: 600;
  color: ${palette.slateLight};
`;

const CurrentName = styled.div`
  font-size: ${typography.size.xl}px;
  font-weight: 700;
  color: ${palette.ink};
  margin-top: 4px;
`;

const CurrentMeta = styled.div`
  color: ${palette.slate};
  margin-top: ${spacing.half};
`;

/* Text left, button right. When the card is too narrow for both, the button
   wraps under the text (full width on a phone) instead of squeezing it.
   min-width: 0 on the text: WebKit's min-width: auto on flex items otherwise
   refuses to shrink it, and Safari pushed the button off the card. */
const CancelRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.sm} ${spacing.md};

  & > button {
    margin-top: 0;
    flex: 0 0 auto;
  }

  ${down('sm')} {
    & > button {
      width: 100%;
    }
  }
`;

const CancelText = styled.div`
  flex: 1 1 260px;
  min-width: 0;
`;

/* THE COMPLIMENTARY STATEMENT. Brand wash, the same tone as ScheduleNote:
   nothing is wrong and nothing is owed — it says what the account is. */
const CompedNote = styled.p`
  margin: ${spacing.sm} 0 0;
  padding: ${spacing.sm};
  border-radius: ${radius.sm};
  background: ${palette.tealWashSoft};
  color: ${palette.inkSoft};
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};

  & b {
    color: ${palette.ink};
    font-weight: ${typography.weight.semibold};
  }

  & a {
    color: ${palette.lagoon};
    font-weight: ${typography.weight.semibold};
  }
`;

/* Figures, buy and return on one centred line. Wrapping happens BETWEEN the
   three groups first, so a phone gets figures / buy / return as three centred
   lines, never a torn group. */
const UnitsBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: ${spacing.sm} ${spacing.lg};
  margin-top: ${spacing.xs};

  & > * {
    min-width: 0;
  }
`;

const Units = styled.section`
  background: ${palette.surface};
  border: 1px solid ${palette.line};
  border-radius: ${radius.md};
  box-shadow: ${shadow.sm};
  padding: ${spacing.lg};
  margin-bottom: ${spacing.lg};
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm};
`;

const UnitsStats = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: ${spacing.lg};
`;

const UnitsStat = styled.div`
  color: ${palette.slate};
  font-size: ${typography.size.sm}px;
  white-space: nowrap;

  & b {
    color: ${palette.ink};
    font-size: ${typography.size.xl}px;
    font-weight: 700;
    margin-right: 8px;
  }
`;

const UnitsHint = styled.p`
  margin: 0;
  color: ${palette.slate};
  font-size: 13px;
`;

/* THE ONLY PLACE OVER-CAP IS VISIBLE. It has to read differently from the three
   figures above it, because the figure it corrects — "0 available" — is one of
   them and looks perfectly healthy. Amber, not coral: the account owes units,
   nothing is broken and nothing is being switched off. */
const OverCap = styled.p`
  margin: 0;
  padding: ${spacing.sm};
  border: 1px solid ${palette.warning};
  border-radius: ${radius.sm};
  background: ${palette.warningSoft};
  color: ${status.warning.fg};
  font-size: 14px;
  line-height: 1.45;

  & b {
    font-weight: 700;
  }
`;

/* Return + restore sit beside the buy row in UnitsBar: quieter verbs on the
   same line, so the pool's three directions (buy, return, restore) share one home. */
const UnitsActions = styled.div`
  display: flex;
  gap: ${spacing.sm};
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;

  & > button {
    margin-top: 0;
  }
`;

const BuyRow = styled.form`
  display: flex;
  gap: ${spacing.sm};
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;

  /* ChooseBtn's own definition (:440-442) carries margin-top: spacing.sm for
     the plan cards, where it is the gap under PlanLimit. Inside this row that
     margin pushed the button 8px below the input's centre and doubled the
     wrapped gap to 32px. Neutralise it HERE, not on ChooseBtn — the row owns
     its own margin-top instead, and the cards keep theirs. */
  & > button {
    margin-top: 0;
  }
`;

const BuyInput = styled.input`
  width: 140px;
  /* Same token AppButton resolves to, so input and button are one height. */
  height: ${layout.controlHeight};
  padding: 0 ${spacing.sm};
  border: 1px solid ${palette.line};
  border-radius: ${radius.sm};
  background: ${palette.surface};
  color: ${palette.ink};
  font-size: ${typography.size.sm}px;

  /* 16px ON A FINGER. iOS zooms the whole page when a text control under 16px
     takes focus and does not zoom back — and this is the field a customer types
     a unit COUNT into, so the zoom lands mid-purchase. Measured at 15px by
     scripts/analytics/responsive.js on every touch shape. */
  ${touch} {
    font-size: 16px;
  }

  &:focus {
    outline: none;
    border-color: ${palette.lagoon};
    box-shadow: ${shadow.focus};
  }
`;

const PlansHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${spacing.sm};
  margin-bottom: ${spacing.sm};
`;

/* The scheduled switch, stated where the plan is. Brand wash, not a warning:
   nothing is wrong, something is booked. */
const ScheduleNote = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${spacing.xs} ${spacing.sm};
  margin-top: ${spacing.sm};
  padding: ${spacing.sm};
  border-radius: ${radius.sm};
  background: ${palette.tealWashSoft};
  color: ${palette.inkSoft};
  font-size: 14px;
  line-height: 1.45;

  & > button {
    margin-left: auto;
  }
`;

const PlanEffective = styled.div`
  color: ${palette.slate};
  font-size: 13px;
  margin-top: -${spacing.half};
`;

const PlansGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: ${spacing.md};
`;

const PlanCard = styled.div<{ current?: boolean }>`
  background: ${palette.surface};
  border: 1.5px solid
    ${({ current }) => (current ? palette.iris : palette.line)};
  border-radius: ${radius.md};
  box-shadow: ${shadow.sm};
  padding: ${spacing.lg};
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PlanName = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: ${palette.ink};
`;

const PlanPrice = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: ${palette.lagoon};
`;

const PlanDesc = styled.div`
  color: ${palette.slate};
  font-size: 14px;
  min-height: 34px;
`;

const PlanLimit = styled.div`
  color: ${palette.inkSoft};
  font-weight: 600;
  font-size: 14px;
`;

const ChooseBtn = styled(AppButton)`
  margin-top: ${spacing.sm};
`;

const CurrentBadge = styled.div`
  margin-top: ${spacing.sm};
  padding: 8px 16px;
  border-radius: ${radius.pill};
  background: ${palette.tealWashSoft};
  color: ${palette.lagoon};
  font-weight: 600;
  text-align: center;
  font-size: 14px;
`;

const ContactLink = styled.a`
  margin-top: ${spacing.sm};
  /* 44px MINIMUM (R-18). 8px of padding around 14px text gave it 37px, which the
     device sweep measured at 320, 390 and 768. It sits in the same row as the
     other plans' Choose buttons, so it was also the odd one out by height. */
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: ${layout.touchTarget};
  padding: 8px 16px;
  border-radius: ${radius.pill};
  background: ${palette.tealWashSoft};
  color: ${palette.lagoon};
  font-weight: 600;
  text-align: center;
  font-size: 14px;
  text-decoration: none;
`;

/* The promo row. A form, so Enter submits it the way a code field should. */
const PromoRow = styled.form`
  display: flex;
  gap: 8px;
  align-items: center;
  margin-top: 6px;
  max-width: 420px;
`;

const PromoInput = styled.input`
  flex: 1;
  min-width: 0;
  height: 38px;
  padding: 0 10px;
  border: 1px solid ${palette.lineStrong};
  border-radius: ${radius.sm};
  background: ${palette.surface};
  color: ${palette.ink};
  font: inherit;
  /* 16px is not a taste: anything smaller makes iOS Safari zoom the page on
     focus (design_system.md §8). */
  font-size: 16px;
  text-transform: uppercase;

  &::placeholder {
    color: ${palette.slateLight};
    text-transform: none;
  }
  &:focus-visible {
    outline: 2px solid ${palette.teal};
    outline-offset: 1px;
  }
`;

const PromoError = styled.p`
  margin: 6px 0 0;
  color: ${palette.alert};
  font-size: ${typography.size.xs}px;
`;

const Note = styled.p`
  margin-top: ${spacing.lg};
  color: ${palette.slate};
`;

/* The server's own refusal, shown verbatim inside the modal. */
const BuyNotice = styled.p`
  margin: 0 0 ${spacing.md};
  padding: ${spacing.sm};
  border-radius: ${radius.sm};
  background: ${palette.tealWashSoft};
  color: ${palette.inkSoft};
  font-size: 14px;
  line-height: 1.45;
`;
